import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2, Copy, Save, X, Calendar as CalendarIcon, ChevronDown, ChevronRight, Package } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useCatalogsComercial } from "@/hooks/useCatalogsComercial";

const emptyItemCambio = () => ({
  sku: "", vpn: "", descripcion: "", clave_vieja: "", clave_nueva: "", upc: "",
  qty: 0, costo_pieza: "", observaciones: "",
});

const emptyItemBundle = () => ({
  sku: "", vpn: "", descripcion: "", upc: "",
  qty: 0, costo_pieza: "", observaciones: "",
  componentes: [{ sku: "", vpn: "", upc: "", qty: 0 }],
});

const emptyComponente = () => ({ sku: "", vpn: "", upc: "", qty: 0 });

const ESTATUS_OPTS = ["Pendiente", "En proceso", "Terminado", "Entregado", "Cancelado"];

function DatePicker({ value, onChange, testId, placeholder }) {
  const date = value ? new Date(value) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" type="button" data-testid={testId}
          className={cn("w-full justify-start h-10 font-normal", !value && "text-muted-foreground")}>
          <CalendarIcon className="h-4 w-4 mr-2" />
          {value ? value : placeholder || "Seleccionar fecha"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

export default function OTACEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { opts, columnasMap, preciosMap } = useCatalogsComercial();

  const { data: ot, isLoading } = useQuery({
    queryKey: ["otac", id],
    queryFn: async () => (await api.get(`/ots-comercial/${id}`)).data,
  });

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [expandedBundles, setExpandedBundles] = useState({});

  useEffect(() => {
    if (ot && !form) {
      const tipoOt = ot.tipo_ot || "Cambio de Clave / UPC";
      const isBdl = tipoOt === "Bundle";
      setForm({
        tipo_ot: tipoOt,
        folio_comercial: ot.folio_comercial || "",
        cliente: ot.cliente || "",
        solicitante: ot.solicitante || "",
        fecha_compromiso: ot.fecha_compromiso || "",
        forma_pago: ot.forma_pago || "",
        area: ot.area || "",
        tipo_trabajo: ot.tipo_trabajo || "",
        requerimientos_ot: ot.requerimientos_ot || (ot.tipo_etiquetado ? [ot.tipo_etiquetado] : []),
        autorizacion_comercializacion: ot.autorizacion_comercializacion || "",
        observaciones_generales: ot.observaciones_generales || "",
        estatus: ot.estatus || "Pendiente",
        costo_maquila: ot.costo_maquila || "",
        costo_comercial: ot.costo_comercial || "",
        items: (ot.items || []).map(it => {
          const base = {
            sku: it.sku || "", vpn: it.vpn || "", descripcion: it.descripcion || "",
            upc: it.upc || "", qty: it.qty || 0, costo_pieza: it.costo_pieza || 0,
            observaciones: it.observaciones || "",
            clave_vieja: it.clave_vieja || it.clave_final || "",
            clave_nueva: it.clave_nueva || "",
          };
          if (isBdl) {
            base.componentes = (it.componentes || []).map(c => ({
              sku: c.sku || "", vpn: c.vpn || "", upc: c.upc || "", qty: c.qty || 0,
            }));
            if (base.componentes.length === 0) base.componentes = [emptyComponente()];
          }
          return base;
        }),
      });
    }
  }, [ot, form]);

  // Auto-set Costo Maquila = total de partidas
  const totalCosto = form ? form.items.reduce((a, b) => a + ((parseInt(b.qty) || 0) * (parseFloat(b.costo_pieza) || 0)), 0) : 0;
  useEffect(() => {
    if (!form) return;
    const rounded = Math.round(totalCosto * 100) / 100;
    setForm(prev => prev ? ({ ...prev, costo_maquila: rounded || "" }) : prev);
  }, [totalCosto]);

  if (isLoading || !form) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>;

  const isBundleMode = form.tipo_ot === "Bundle";
  const currentCols = columnasMap[form.tipo_ot] || [];

  const setField = (k, v) => {
    if (k === "tipo_ot" && v !== form.tipo_ot) {
      setForm({ ...form, tipo_ot: v });
      return;
    }
    // Auto-fill Costo/Pza from Precios x Trabajo when tipo_trabajo changes
    if (k === "tipo_trabajo") {
      const precio = preciosMap[v];
      if (precio !== undefined && precio > 0) {
        const updatedItems = form.items.map(item => ({ ...item, costo_pieza: precio }));
        setForm({ ...form, tipo_trabajo: v, items: updatedItems });
        toast.success(`Costo/Pza actualizado a ${fmtMoney(precio)} por "${v}"`);
        return;
      }
    }
    setForm({ ...form, [k]: v });
  };

  const toggleRequerimiento = (req) => {
    const current = form.requerimientos_ot || [];
    const next = current.includes(req) ? current.filter(r => r !== req) : [...current, req];
    setForm({ ...form, requerimientos_ot: next });
  };
  const setItem = (idx, k, v) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [k]: v };
    setForm({ ...form, items });
  };
  const setComponente = (itemIdx, compIdx, k, v) => {
    const items = [...form.items];
    const comps = [...(items[itemIdx].componentes || [])];
    comps[compIdx] = { ...comps[compIdx], [k]: v };
    items[itemIdx] = { ...items[itemIdx], componentes: comps };
    setForm({ ...form, items });
  };
  const addComponente = (itemIdx) => {
    const items = [...form.items];
    const comps = [...(items[itemIdx].componentes || []), emptyComponente()];
    items[itemIdx] = { ...items[itemIdx], componentes: comps };
    setForm({ ...form, items });
  };
  const delComponente = (itemIdx, compIdx) => {
    const items = [...form.items];
    const comps = (items[itemIdx].componentes || []).filter((_, i) => i !== compIdx);
    items[itemIdx] = { ...items[itemIdx], componentes: comps };
    setForm({ ...form, items });
  };

  const addRow = () => setForm({ ...form, items: [...form.items, isBundleMode ? emptyItemBundle() : emptyItemCambio()] });
  const dupRow = (idx) => {
    const dup = JSON.parse(JSON.stringify(form.items[idx]));
    setForm({ ...form, items: [...form.items.slice(0, idx + 1), dup, ...form.items.slice(idx + 1)] });
  };
  const delRow = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const toggleBundle = (idx) => setExpandedBundles(prev => ({ ...prev, [idx]: !prev[idx] }));

  const totalQty = form.items.reduce((a, b) => a + (parseInt(b.qty) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cliente) return toast.error("Selecciona un cliente");
    if (!form.solicitante) return toast.error("Selecciona un solicitante");
    setSaving(true);
    try {
      await api.patch(`/ots-comercial/${id}`, form);
      toast.success(`OT Comercial ${ot.folio} actualizada`);
      navigate(`/ots-comercial/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="otac-edit-form">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Editar {ot.folio}</h1>
          <p className="text-sm text-muted-foreground">Modifica los datos de la OT Comercial.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={() => navigate(`/ots-comercial/${id}`)} data-testid="otac-edit-cancel">
            <X className="h-4 w-4 mr-1" /> Cancelar
          </Button>
          <Button type="submit" disabled={saving} data-testid="otac-edit-save">
            <Save className="h-4 w-4 mr-1" /> {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>

      {/* Header fields */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Datos Generales</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tipo de OT</Label>
              <Select value={form.tipo_ot} onValueChange={(v) => setField("tipo_ot", v)}>
                <SelectTrigger data-testid="otac-tipo-ot-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {opts.tipos_ot.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Folio Comercial</Label>
              <Input value={form.folio_comercial} onChange={(e) => setField("folio_comercial", e.target.value)} placeholder="Folio del área comercial" data-testid="otac-folio-comercial-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cliente *</Label>
              <Select value={form.cliente} onValueChange={(v) => setField("cliente", v)}>
                <SelectTrigger data-testid="otac-cliente-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {opts.clientes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Solicitante *</Label>
              <Select value={form.solicitante} onValueChange={(v) => setField("solicitante", v)}>
                <SelectTrigger data-testid="otac-solicitante-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  {opts.solicitantes.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Fecha Compromiso Entrega</Label>
              <DatePicker value={form.fecha_compromiso} onChange={(v) => setField("fecha_compromiso", v)} testId="otac-fecha-compromiso" placeholder="Fecha compromiso" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Forma de Pago</Label>
              <Select value={form.forma_pago} onValueChange={(v) => setField("forma_pago", v)}>
                <SelectTrigger data-testid="otac-forma-pago-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{opts.formas_pago.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{"Área"}</Label>
              <Select value={form.area} onValueChange={(v) => setField("area", v)}>
                <SelectTrigger data-testid="otac-area-input"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{opts.areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tipo de Trabajo</Label>
              <Select value={form.tipo_trabajo} onValueChange={(v) => setField("tipo_trabajo", v)}>
                <SelectTrigger data-testid="otac-tipo-trabajo-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{opts.tipos_trabajo.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Requerimientos de OT</Label>
              <div className="flex flex-wrap gap-2 mt-1.5 p-2 rounded-md border border-border min-h-[36px]">
                {opts.requerimientos_ot.length === 0 && <span className="text-xs text-muted-foreground">Sin opciones en catálogo</span>}
                {opts.requerimientos_ot.map(req => (
                  <label key={req} className="flex items-center gap-1.5 cursor-pointer text-xs hover:bg-muted/50 px-2 py-1 rounded">
                    <input type="checkbox" checked={(form.requerimientos_ot || []).includes(req)} onChange={() => toggleRequerimiento(req)} className="rounded border-border" />
                    {req}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{"Autorización Comercialización"}</Label>
              <Select value={form.autorizacion_comercializacion} onValueChange={(v) => setField("autorizacion_comercializacion", v)}>
                <SelectTrigger data-testid="otac-autorizacion-input"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{opts.autorizaciones.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Estatus</Label>
              <Select value={form.estatus} onValueChange={(v) => setField("estatus", v)}>
                <SelectTrigger data-testid="otac-estatus-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ESTATUS_OPTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Observaciones Generales</Label>
              <Textarea value={form.observaciones_generales} onChange={(e) => setField("observaciones_generales", e.target.value)} rows={2} data-testid="otac-observaciones" />
            </div>
          </div>
          {/* Costos Maquila / Comercial */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-border/60 mt-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Costo Maquila (automático del total de partidas)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-mono">$</span>
                <Input type="number" step="0.01" min="0" className="pl-7 font-mono bg-muted/30" value={form.costo_maquila} readOnly tabIndex={-1} placeholder="0.00" data-testid="otac-costo-maquila" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Costo Comercial (lo que cobras al área comercial)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-mono">$</span>
                <Input type="number" step="0.01" min="0" className="pl-7 font-mono" value={form.costo_comercial} onChange={(e) => setField("costo_comercial", e.target.value)} placeholder="0.00" data-testid="otac-costo-comercial" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items / Partidas */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm">Partidas {isBundleMode ? "(Bundles)" : `(${form.tipo_ot || "Selecciona tipo"})`}</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="font-mono tabular-nums text-xs py-1 px-2">QTY: {totalQty}</Badge>
              <Badge variant="outline" className="font-mono tabular-nums text-xs py-1 px-2">Total: {fmtMoney(totalCosto)}</Badge>
              <Button type="button" size="sm" onClick={addRow} data-testid="otac-add-row">
                <Plus className="h-3.5 w-3.5 mr-1" /> {isBundleMode ? "Bundle" : "Partida"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {/* ─── DYNAMIC COLUMNS TABLE ─── */}
            {!isBundleMode && (
              <table className="w-full text-xs min-w-[900px]">
                <thead className="bg-muted/40">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 px-2 w-8">#</th>
                    {currentCols.map(col => (
                      <th key={col.key} className={`py-2 px-2 ${col.key === "costo_pieza" ? "w-28" : col.key === "qty" ? "w-20" : ""}`}>{col.label}</th>
                    ))}
                    <th className="py-2 px-2 w-24">Total</th>
                    <th className="py-2 px-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, idx) => {
                    const rowTotal = (parseInt(it.qty) || 0) * (parseFloat(it.costo_pieza) || 0);
                    return (
                      <tr key={idx} className="border-t border-border/60" data-testid={`otac-item-row-${idx}`}>
                        <td className="py-1.5 px-2 text-muted-foreground font-mono">{idx + 1}</td>
                        {currentCols.map(col => (
                          <td key={col.key} className="py-1.5 px-2">
                            {col.key === "costo_pieza" ? (
                              <Input className="h-9 text-sm font-mono text-right w-28" type="number" step="0.01" min="0" value={it[col.key] || ""} onChange={(e) => setItem(idx, col.key, e.target.value)} />
                            ) : col.key === "qty" ? (
                              <Input className="h-9 text-sm font-mono text-right w-20" type="number" min="0" value={it[col.key] || ""} onChange={(e) => setItem(idx, col.key, e.target.value)} />
                            ) : col.tipo === "number" ? (
                              <Input className="h-8 text-xs font-mono" type="number" min="0" value={it[col.key] || ""} onChange={(e) => setItem(idx, col.key, e.target.value)} />
                            ) : (
                              <Input className="h-8 text-xs" value={it[col.key] || ""} onChange={(e) => setItem(idx, col.key, e.target.value)} />
                            )}
                          </td>
                        ))}
                        <td className="py-1.5 px-2 font-mono tabular-nums text-right text-primary font-semibold text-sm">{fmtMoney(rowTotal)}</td>
                        <td className="py-1.5 px-2">
                          <div className="flex gap-0.5">
                            <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => dupRow(idx)}><Copy className="h-3 w-3" /></Button>
                            <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => delRow(idx)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* ─── BUNDLE TABLE ─── */}
            {isBundleMode && (
              <div className="divide-y divide-border">
                {form.items.map((it, idx) => {
                  const rowTotal = (parseInt(it.qty) || 0) * (parseFloat(it.costo_pieza) || 0);
                  const isExpanded = expandedBundles[idx] !== false;
                  const comps = it.componentes || [];
                  return (
                    <div key={idx} className="p-3 space-y-2" data-testid={`otac-bundle-row-${idx}`}>
                      <div className="flex items-center gap-2">
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => toggleBundle(idx)}>
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                        <Badge variant="secondary" className="shrink-0 text-[10px]"><Package className="h-3 w-3 mr-1" /> Bundle {idx + 1}</Badge>
                        <div className="grid grid-cols-7 gap-2 flex-1 items-center">
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Clave Bundle</Label>
                            <Input className="h-8 text-xs font-mono" value={it.sku} onChange={(e) => setItem(idx, "sku", e.target.value)} placeholder="Clave" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">VPN</Label>
                            <Input className="h-8 text-xs" value={it.vpn} onChange={(e) => setItem(idx, "vpn", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Descripción</Label>
                            <Input className="h-8 text-xs" value={it.descripcion} onChange={(e) => setItem(idx, "descripcion", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">UPC Bundle</Label>
                            <Input className="h-8 text-xs font-mono" value={it.upc} onChange={(e) => setItem(idx, "upc", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">QTY</Label>
                            <Input className="h-9 text-sm font-mono text-right" type="number" min="0" value={it.qty} onChange={(e) => setItem(idx, "qty", e.target.value)} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Costo/Pza</Label>
                            <Input className="h-9 text-sm font-mono text-right" type="number" step="0.01" min="0" value={it.costo_pieza} onChange={(e) => setItem(idx, "costo_pieza", e.target.value)} placeholder="0.00" />
                          </div>
                          <div className="flex items-end gap-1">
                            <div className="flex-1">
                              <Label className="text-[10px] text-muted-foreground">Total</Label>
                              <div className="h-9 flex items-center font-mono tabular-nums text-right text-primary font-semibold text-sm">{fmtMoney(rowTotal)}</div>
                            </div>
                            <div className="flex gap-0.5 pb-0.5">
                              <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => dupRow(idx)}><Copy className="h-3 w-3" /></Button>
                              <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => delRow(idx)}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="ml-10 bg-muted/20 rounded-md border border-border/50 p-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Componentes del Bundle</span>
                            <Button type="button" size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => addComponente(idx)}>
                              <Plus className="h-3 w-3 mr-1" /> Componente
                            </Button>
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                                <th className="py-1 px-2 w-8">#</th>
                                <th className="py-1 px-2">SKU</th>
                                <th className="py-1 px-2">VPN</th>
                                <th className="py-1 px-2">UPC</th>
                                <th className="py-1 px-2 w-28">Piezas</th>
                                <th className="py-1 px-2 w-10"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {comps.map((comp, ci) => (
                                <tr key={ci} className="border-t border-border/40">
                                  <td className="py-1 px-2 text-muted-foreground font-mono">{ci + 1}</td>
                                  <td className="py-1 px-2"><Input className="h-7 text-xs font-mono" value={comp.sku} onChange={(e) => setComponente(idx, ci, "sku", e.target.value)} placeholder="SKU comp." /></td>
                                  <td className="py-1 px-2"><Input className="h-7 text-xs" value={comp.vpn} onChange={(e) => setComponente(idx, ci, "vpn", e.target.value)} placeholder="VPN comp." /></td>
                                  <td className="py-1 px-2"><Input className="h-7 text-xs font-mono" value={comp.upc} onChange={(e) => setComponente(idx, ci, "upc", e.target.value)} placeholder="UPC comp." /></td>
                                  <td className="py-1 px-2"><Input className="h-8 text-sm font-mono text-right" type="number" min="0" value={comp.qty} onChange={(e) => setComponente(idx, ci, "qty", e.target.value)} /></td>
                                  <td className="py-1 px-2">
                                    <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => delComponente(idx, ci)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                              {comps.length === 0 && (
                                <tr><td colSpan={6} className="py-3 text-center text-muted-foreground text-xs">Sin componentes.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
